import { useEffect, useMemo, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "~/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "~/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { useAccountsByCompany } from "~/hooks/contabilidad/useAccounts";
import { useClassificationGroups } from "~/hooks/contabilidad/useClassificationGroups";
import { cn } from "~/lib/utils";
import type { AccountMapping, AccountMappingFormData } from "~/types/contabilidad";
import {
  CLASE_OPTIONS,
  REPORTE_OPTIONS,
  SUB_CLASE_OPTIONS,
  SUB_CLASE_2_OPTIONS,
  TIPO_OPTIONS,
} from "~/types/contabilidad";

const formSchema = z.object({
  account_id: z.string().min(1, "Cuenta es requerida"),
  reporte: z.enum(["Balance", "Resultados"]),
  clase: z.enum([
    "Activo",
    "Pasivo y Patrimonio",
    "Costos",
    "Gastos",
    "Ingresos",
  ]),
  orden_clase: z.coerce.number().int().min(0).default(0),
  orden_tipo: z.coerce.number().int().min(0).default(0),
  tipo: z.string().optional().nullable(),
  sub_clase: z.string().min(1, "Requerido"),
  orden_subclase: z.coerce.number().int().min(0).default(0),
  sub_clase_2: z.string().min(1, "Requerido"),
  orden_subclase_2: z.coerce.number().int().min(0).default(0),
  grupo_cuenta_id: z.string().optional().nullable(),
  orden_grupo_cuenta: z.coerce.number().int().min(0).default(0),
  clave: z.coerce.number().int().optional().nullable(),
  signo: z.coerce.number().int().optional().nullable(),
  clase_1: z.string().optional().nullable(),
});

type FormData = z.infer<typeof formSchema>;

interface MapeoEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mapping?: AccountMapping;
  templateId: string;
  companyId: string;
  onSubmit: (data: AccountMappingFormData) => void;
  isLoading?: boolean;
}

export function MapeoEditDialog({
  open,
  onOpenChange,
  mapping,
  templateId,
  companyId,
  onSubmit,
  isLoading,
}: MapeoEditDialogProps) {
  const { data: classificationGroups = [] } = useClassificationGroups({
    company_id: companyId,
  });
  const { data: accounts = [] } = useAccountsByCompany(companyId);
  const [accountPopoverOpen, setAccountPopoverOpen] = useState(false);
  const [subClasePopoverOpen, setSubClasePopoverOpen] = useState(false);
  const [subClase2PopoverOpen, setSubClase2PopoverOpen] = useState(false);

  const isEditMode = !!mapping;

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      account_id: "",
      reporte: "Balance",
      clase: "Activo",
      orden_clase: 0,
      orden_tipo: 0,
      tipo: null,
      sub_clase: "",
      orden_subclase: 0,
      sub_clase_2: "",
      orden_subclase_2: 0,
      grupo_cuenta_id: null,
      orden_grupo_cuenta: 0,
      clave: null,
      signo: null,
      clase_1: null,
    },
  });

  // Reset form when mapping changes or dialog opens
  useEffect(() => {
    if (open) {
      if (mapping) {
        form.reset({
          account_id: mapping.account_id,
          reporte: mapping.reporte,
          clase: mapping.clase,
          orden_clase: mapping.orden_clase ?? 0,
          orden_tipo: mapping.orden_tipo ?? 0,
          tipo: mapping.tipo,
          sub_clase: mapping.sub_clase,
          orden_subclase: mapping.orden_subclase ?? 0,
          sub_clase_2: mapping.sub_clase_2,
          orden_subclase_2: mapping.orden_subclase_2 ?? 0,
          grupo_cuenta_id: mapping.grupo_cuenta_id,
          orden_grupo_cuenta: mapping.orden_grupo_cuenta ?? 0,
          clave: mapping.clave,
          signo: mapping.signo,
          clase_1: mapping.clase_1,
        });
      } else {
        form.reset({
          account_id: "",
          reporte: "Balance",
          clase: "Activo",
          orden_clase: 0,
          orden_tipo: 0,
          tipo: null,
          sub_clase: "",
          orden_subclase: 0,
          sub_clase_2: "",
          orden_subclase_2: 0,
          grupo_cuenta_id: null,
          orden_grupo_cuenta: 0,
          clave: null,
          signo: null,
          clase_1: null,
        });
      }
    }
  }, [mapping, open, form]);

  const handleFormSubmit = (data: FormData) => {
    onSubmit({
      template_id: templateId,
      account_id: data.account_id,
      reporte: data.reporte,
      clase: data.clase,
      orden_clase: data.orden_clase,
      orden_tipo: data.orden_tipo,
      tipo: data.tipo || undefined,
      sub_clase: data.sub_clase,
      orden_subclase: data.orden_subclase,
      sub_clase_2: data.sub_clase_2,
      orden_subclase_2: data.orden_subclase_2,
      grupo_cuenta_id: data.grupo_cuenta_id || undefined,
      orden_grupo_cuenta: data.orden_grupo_cuenta,
      clave: data.clave ?? undefined,
      signo: data.signo ?? undefined,
      clase_1: data.clase_1 || undefined,
    });
  };

  // Memoized sorted groups for the dropdown
  const sortedGroups = useMemo(() => {
    return [...classificationGroups].sort((a, b) => a.orden - b.orden);
  }, [classificationGroups]);

  // Sorted accounts for the dropdown
  const sortedAccounts = useMemo(() => {
    return [...accounts].sort((a, b) => a.code.localeCompare(b.code));
  }, [accounts]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? "Editar Mapeo de Cuenta" : "Agregar Mapeo de Cuenta"}
          </DialogTitle>
          <DialogDescription>
            {isEditMode
              ? `Editando cuenta: ${mapping.account?.code} - ${mapping.account?.name}`
              : "Seleccione una cuenta y configure sus campos de clasificación."}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleFormSubmit)}
            className="space-y-4"
          >
            {/* Account selector - only show when creating */}
            {!isEditMode && (
              <FormField
                control={form.control}
                name="account_id"
                render={({ field }) => {
                  const selectedAccount = sortedAccounts.find(
                    (acc) => acc.id === field.value
                  );
                  return (
                    <FormItem className="flex flex-col">
                      <FormLabel>Cuenta *</FormLabel>
                      <Popover
                        open={accountPopoverOpen}
                        onOpenChange={setAccountPopoverOpen}
                      >
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              role="combobox"
                              aria-expanded={accountPopoverOpen}
                              className={cn(
                                "h-auto min-h-[2.5rem] w-full justify-between px-3 py-2 font-normal",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              <span className="truncate text-left">
                                {selectedAccount
                                  ? `${selectedAccount.code} - ${selectedAccount.name}`
                                  : "Buscar cuenta..."}
                              </span>
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent
                          className="w-[450px] p-0"
                          align="start"
                          style={{ pointerEvents: "auto" }}
                          onOpenAutoFocus={(e) => e.preventDefault()}
                        >
                          <Command style={{ pointerEvents: "auto" }}>
                            <CommandInput placeholder="Buscar por código o nombre..." />
                            <CommandList>
                              <CommandEmpty>
                                No se encontraron cuentas.
                              </CommandEmpty>
                              <CommandGroup className="max-h-[300px] overflow-auto">
                                {sortedAccounts.map((account) => (
                                  <CommandItem
                                    key={account.id}
                                    value={`${account.code} ${account.name}`}
                                    onSelect={() => {
                                      field.onChange(account.id);
                                      setAccountPopoverOpen(false);
                                    }}
                                    className="cursor-pointer"
                                    style={{ pointerEvents: "auto" }}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4",
                                        field.value === account.id
                                          ? "opacity-100"
                                          : "opacity-0"
                                      )}
                                    />
                                    <span className="font-mono text-sm">
                                      {account.code}
                                    </span>
                                    <span className="ml-2 truncate">
                                      {account.name}
                                    </span>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            )}

            <div className="grid grid-cols-2 gap-4">
              {/* Reporte */}
              <FormField
                control={form.control}
                name="reporte"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Reporte</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar..." />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {REPORTE_OPTIONS.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Clase */}
              <FormField
                control={form.control}
                name="clase"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Clase</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar..." />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {CLASE_OPTIONS.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              {/* Orden Clase */}
              <FormField
                control={form.control}
                name="orden_clase"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Orden Clase</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Orden Tipo */}
              <FormField
                control={form.control}
                name="orden_tipo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Orden Tipo</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Tipo */}
              <FormField
                control={form.control}
                name="tipo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo</FormLabel>
                    <Select
                      onValueChange={(value) =>
                        field.onChange(value === "__none__" ? null : value)
                      }
                      defaultValue={field.value || "__none__"}
                      value={field.value || "__none__"}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar..." />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="__none__">Sin tipo</SelectItem>
                        {TIPO_OPTIONS.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Sub Clase */}
              <FormField
                control={form.control}
                name="sub_clase"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Sub Clase</FormLabel>
                    <Popover
                      open={subClasePopoverOpen}
                      onOpenChange={setSubClasePopoverOpen}
                    >
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={subClasePopoverOpen}
                            className={cn(
                              "w-full justify-between font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            <span className="truncate">
                              {field.value || "Seleccionar..."}
                            </span>
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-[250px] p-0"
                        align="start"
                        style={{ pointerEvents: "auto" }}
                        onOpenAutoFocus={(e) => e.preventDefault()}
                      >
                        <Command style={{ pointerEvents: "auto" }}>
                          <CommandInput placeholder="Buscar..." />
                          <CommandList>
                            <CommandEmpty>No encontrado.</CommandEmpty>
                            <CommandGroup className="max-h-[200px] overflow-auto">
                              {SUB_CLASE_OPTIONS.map((option) => (
                                <CommandItem
                                  key={option}
                                  value={option}
                                  onSelect={() => {
                                    field.onChange(option);
                                    setSubClasePopoverOpen(false);
                                  }}
                                  className="cursor-pointer"
                                  style={{ pointerEvents: "auto" }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      field.value === option
                                        ? "opacity-100"
                                        : "opacity-0"
                                    )}
                                  />
                                  {option}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Orden Subclase */}
              <FormField
                control={form.control}
                name="orden_subclase"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Orden Subclase</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* SubClase 2 */}
              <FormField
                control={form.control}
                name="sub_clase_2"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>SubClase 2</FormLabel>
                    <Popover
                      open={subClase2PopoverOpen}
                      onOpenChange={setSubClase2PopoverOpen}
                    >
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={subClase2PopoverOpen}
                            className={cn(
                              "w-full justify-between font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            <span className="truncate">
                              {field.value || "Seleccionar..."}
                            </span>
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-[280px] p-0"
                        align="start"
                        style={{ pointerEvents: "auto" }}
                        onOpenAutoFocus={(e) => e.preventDefault()}
                      >
                        <Command style={{ pointerEvents: "auto" }}>
                          <CommandInput placeholder="Buscar..." />
                          <CommandList>
                            <CommandEmpty>No encontrado.</CommandEmpty>
                            <CommandGroup className="max-h-[200px] overflow-auto">
                              {SUB_CLASE_2_OPTIONS.map((option) => (
                                <CommandItem
                                  key={option}
                                  value={option}
                                  onSelect={() => {
                                    field.onChange(option);
                                    setSubClase2PopoverOpen(false);
                                  }}
                                  className="cursor-pointer"
                                  style={{ pointerEvents: "auto" }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      field.value === option
                                        ? "opacity-100"
                                        : "opacity-0"
                                    )}
                                  />
                                  {option}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Orden Subclase 2 */}
              <FormField
                control={form.control}
                name="orden_subclase_2"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Orden Subclase 2</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Grupo Cuenta */}
              <FormField
                control={form.control}
                name="grupo_cuenta_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Grupo Cuenta</FormLabel>
                    <Select
                      onValueChange={(value) =>
                        field.onChange(value === "__none__" ? null : value)
                      }
                      defaultValue={field.value || "__none__"}
                      value={field.value || "__none__"}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar grupo..." />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="__none__">Sin grupo</SelectItem>
                        {sortedGroups.map((group) => (
                          <SelectItem key={group.id} value={group.id}>
                            {group.code} - {group.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Orden Grupo Cuenta */}
              <FormField
                control={form.control}
                name="orden_grupo_cuenta"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Orden Grupo Cta.</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              {/* Clave */}
              <FormField
                control={form.control}
                name="clave"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Clave</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        value={field.value ?? ""}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value ? Number(e.target.value) : null
                          )
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Signo */}
              <FormField
                control={form.control}
                name="signo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Signo</FormLabel>
                    <Select
                      onValueChange={(value) =>
                        field.onChange(value === "__none__" ? null : Number(value))
                      }
                      defaultValue={
                        field.value === null ? "__none__" : String(field.value)
                      }
                      value={
                        field.value === null ? "__none__" : String(field.value)
                      }
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar..." />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="__none__">Sin signo</SelectItem>
                        <SelectItem value="-1">-1</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Clase 1 */}
              <FormField
                control={form.control}
                name="clase_1"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Clase 1</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ""}
                        onChange={(e) =>
                          field.onChange(e.target.value || null)
                        }
                        placeholder="Ej: Ventas"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Guardar
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
